import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { sessionManager } from './sessionManager';
import { authService } from './authService';
import SessionWarning from './SessionWarning';

interface SessionContextType {
  isManagerLoggedIn: boolean;
  remainingTime: number;
  refreshSession: () => void;
  logout: () => void;
  user: any | null;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

interface SessionProviderProps {
  children: ReactNode;
}

export const SessionProvider: React.FC<SessionProviderProps> = ({ children }) => {
  const [isManagerLoggedIn, setIsManagerLoggedIn] = useState(false);
  const [remainingTime, setRemainingTime] = useState(25);
  const [showWarning, setShowWarning] = useState(false);
  const [warningMinutes, setWarningMinutes] = useState(5);
  const [user, setUser] = useState<any | null>(null);

  const navigate = useNavigate();
  const location = useLocation();

  // Monitor login state changes
  useEffect(() => {
    const checkLoginState = () => {
      const currentlyLoggedIn = sessionManager.isManagerLoggedIn();
      if (currentlyLoggedIn !== isManagerLoggedIn) {
        setIsManagerLoggedIn(currentlyLoggedIn);

        if (currentlyLoggedIn) {
          sessionManager.restart();
          setShowWarning(false);
          setUser(authService.getUserData());
          console.log('Manager login detected, session management restarted');
        } else {
          setShowWarning(false);
          setUser(null);
          console.log('Manager logout detected');
        }
      }
    };

    checkLoginState();

    const interval = setInterval(checkLoginState, 5000);

    return () => clearInterval(interval);
  }, [location, isManagerLoggedIn]);

  const handleSessionExpired = useCallback(() => {
    setShowWarning(false);
    setIsManagerLoggedIn(false);
    setUser(null);

    sessionManager.clearManagerSession();
    sessionManager.cleanup();

    window.dispatchEvent(new CustomEvent('authChanged', { detail: { type: 'session_expired' } }));

    navigate('/login?session_expired=true', { replace: true });
  }, [navigate]);

  const handleExtendSession = () => {
    sessionManager.refreshSession();
    setShowWarning(false);
    console.log('Session extended by user');
  };

  const handleLogoutFromWarning = () => {
    logout();
  };

  // Monitor session state and setup callbacks
  useEffect(() => {
    setIsManagerLoggedIn(sessionManager.isManagerLoggedIn());
    setUser(authService.getUserData());

    sessionManager.onSessionWarning((minutes: number) => {
      setWarningMinutes(minutes);
      setShowWarning(true);
      console.log(`Session warning triggered: ${minutes} minutes remaining`);
    });

    sessionManager.onSessionExpired(() => {
      console.log('Session expired, redirecting to login');
      handleSessionExpired();
    });

    const interval = setInterval(() => {
      setRemainingTime(sessionManager.getRemainingSessionTime());
    }, 30000);

    return () => {
      clearInterval(interval);
    };
  }, [handleSessionExpired]);

  const refreshSession = () => {
    sessionManager.refreshSession();
    setRemainingTime(sessionManager.getRemainingSessionTime());
  };

  const logout = () => {
    sessionManager.clearManagerSession();
    sessionManager.cleanup();
    authService.clearAuth();
    setIsManagerLoggedIn(false);
    setShowWarning(false);
    setUser(null);

    navigate('/login', { replace: true });
  };

  const contextValue: SessionContextType = {
    isManagerLoggedIn,
    remainingTime,
    refreshSession,
    logout,
    user
  };

  return (
    <SessionContext.Provider value={contextValue}>
      {children}

      <SessionWarning
        remainingMinutes={warningMinutes}
        onExtendSession={handleExtendSession}
        onLogout={handleLogoutFromWarning}
        visible={showWarning}
      />
    </SessionContext.Provider>
  );
};

export const useSession = () => {
  const context = useContext(SessionContext);
  if (context === undefined) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
};

export default SessionProvider;
